import type { SlaUnit, TaskPriority, TaskTemplate, TaskTemplateInput } from '@pm/schemas';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Label,
  Select,
  Textarea,
} from '@pm/ui';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { PRIORITY_LABEL } from '@/lib/format';
import { useFunctions } from '@/lib/queries';

/** Template de tarefa (etapa ou bloco): título, função, SLA, aprovação, obrigatoriedade, checklist e dependências. */
export function TaskTemplateDialog({
  open,
  onOpenChange,
  template,
  siblings,
  isSubtask,
  onSubmit,
  loading,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  template?: TaskTemplate;
  /** Outros templates do mesmo grupo (para dependências). */
  siblings: TaskTemplate[];
  isSubtask?: boolean;
  onSubmit: (input: TaskTemplateInput, dependsOn: string[]) => void;
  loading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={template ? 'Editar template de tarefa' : 'Novo template de tarefa'}
        size="lg"
      >
        <TemplateForm
          template={template}
          siblings={siblings}
          isSubtask={isSubtask}
          onSubmit={onSubmit}
          loading={loading}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function TemplateForm({
  template,
  siblings,
  onSubmit,
  loading,
  onCancel,
}: {
  template?: TaskTemplate;
  siblings: TaskTemplate[];
  isSubtask?: boolean;
  onSubmit: (i: TaskTemplateInput, d: string[]) => void;
  loading: boolean;
  onCancel: () => void;
}) {
  const { data: functions = [] } = useFunctions();
  const [title, setTitle] = useState(template?.title ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [functionId, setFunctionId] = useState<string>(template?.function?.id ?? '');
  const [slaValue, setSlaValue] = useState<string>(template?.sla_value?.toString() ?? '');
  const [slaUnit, setSlaUnit] = useState<SlaUnit>(template?.sla_unit ?? 'BUSINESS_DAYS');
  const [priority, setPriority] = useState<TaskPriority>(template?.priority ?? 'NORMAL');
  const [requiresApproval, setRequiresApproval] = useState(template?.requires_approval ?? false);
  const [isRequired, setIsRequired] = useState(template?.is_required ?? true);
  const [checklists, setChecklists] = useState<{ title: string; items: string[] }[]>(
    template?.checklists ?? [],
  );
  const [dependsOn, setDependsOn] = useState<string[]>(template?.depends_on ?? []);
  const others = siblings.filter((s) => s.id !== template?.id);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(
          {
            title,
            description: description || null,
            function_id: functionId || null,
            sla_value: slaValue === '' ? null : Number(slaValue),
            sla_unit: slaValue === '' ? null : slaUnit,
            priority,
            requires_approval: requiresApproval,
            is_required: isRequired,
            checklists: checklists
              .filter((c) => c.title.trim())
              .map((c) => ({ title: c.title, items: c.items.filter((i) => i.trim()) })),
          },
          dependsOn,
        );
      }}
    >
      <Field label="Título" htmlFor="tt-title">
        <Input
          id="tt-title"
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex.: Configurar pixel"
        />
      </Field>
      <Field label="Descrição" htmlFor="tt-desc">
        <Textarea
          id="tt-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="min-h-[72px]"
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field
          label="Função responsável"
          htmlFor="tt-fn"
          hint="Resolve para a pessoa com a função no cliente; senão, no time."
        >
          <Select id="tt-fn" value={functionId} onChange={(e) => setFunctionId(e.target.value)}>
            <option value="">Sem função (não atribuída)</option>
            {functions.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="SLA" htmlFor="tt-sla">
          <div className="flex gap-2">
            <Input
              id="tt-sla"
              type="number"
              min={0}
              mono
              value={slaValue}
              onChange={(e) => setSlaValue(e.target.value)}
              placeholder="—"
              className="w-24"
            />
            <Select value={slaUnit} onChange={(e) => setSlaUnit(e.target.value as SlaUnit)}>
              <option value="BUSINESS_DAYS">dias úteis</option>
              <option value="DAYS">dias</option>
              <option value="HOURS">horas</option>
            </Select>
          </div>
        </Field>
        <Field label="Prioridade" htmlFor="tt-prio">
          <Select
            id="tt-prio"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
          >
            {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const).map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABEL[p]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="flex flex-wrap gap-5">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={isRequired} onCheckedChange={(v) => setIsRequired(v === true)} />{' '}
          Obrigatória para concluir a etapa
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={requiresApproval}
            onCheckedChange={(v) => setRequiresApproval(v === true)}
          />{' '}
          Exige aprovação
        </label>
      </div>

      {others.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <Label>Depende de (mesma etapa/bloco)</Label>
          <div className="flex flex-wrap gap-2">
            {others.map((s) => (
              <label
                key={s.id}
                className="flex items-center gap-2 rounded border border-border px-2 py-1 text-sm"
              >
                <Checkbox
                  checked={dependsOn.includes(s.id)}
                  onCheckedChange={(v) =>
                    setDependsOn(v ? [...dependsOn, s.id] : dependsOn.filter((x) => x !== s.id))
                  }
                />
                {s.title}
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Label>Checklists</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setChecklists([...checklists, { title: '', items: [''] }])}
          >
            <Plus /> Checklist
          </Button>
        </div>
        {checklists.map((c, ci) => (
          <div key={ci} className="flex flex-col gap-1.5 rounded border border-border p-2">
            <div className="flex gap-2">
              <Input
                placeholder="Título da checklist"
                value={c.title}
                onChange={(e) =>
                  setChecklists(
                    checklists.map((x, i) => (i === ci ? { ...x, title: e.target.value } : x)),
                  )
                }
                className="h-control-sm text-sm"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Remover checklist"
                onClick={() => setChecklists(checklists.filter((_, i) => i !== ci))}
              >
                <X />
              </Button>
            </div>
            {c.items.map((it, ii) => (
              <div key={ii} className="flex gap-2 pl-4">
                <Input
                  placeholder="Item"
                  value={it}
                  onChange={(e) =>
                    setChecklists(
                      checklists.map((x, i) =>
                        i === ci
                          ? { ...x, items: x.items.map((y, j) => (j === ii ? e.target.value : y)) }
                          : x,
                      ),
                    )
                  }
                  className="h-control-sm text-sm"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remover item"
                  onClick={() =>
                    setChecklists(
                      checklists.map((x, i) =>
                        i === ci ? { ...x, items: x.items.filter((_, j) => j !== ii) } : x,
                      ),
                    )
                  }
                >
                  <X />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start pl-4"
              onClick={() =>
                setChecklists(
                  checklists.map((x, i) => (i === ci ? { ...x, items: [...x.items, ''] } : x)),
                )
              }
            >
              <Plus /> Item
            </Button>
          </div>
        ))}
      </div>

      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" loading={loading} disabled={!title.trim()}>
          Salvar
        </Button>
      </DialogFooter>
    </form>
  );
}
